import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import {
  assignReviewCase,
  createReviewCaseAction,
  createReviewCaseNote,
  fetchReviewAssistantDrafts,
  fetchReviewAssistantSummary,
  fetchReviewCaseDetail,
  fetchReviewSnapshot,
  recordReviewAssistantDraftDecision,
  updateReviewCaseStatus,
} from '../utils/agentUiApi';
import {
  ACTION_LABELS,
  ACTION_TYPE_OPTIONS,
  ARTIFACT_LABELS,
  EVENT_LABELS,
  STATUS_WRITE_OPTIONS,
  InlineMessage,
  ReviewChip,
  SectionTitle,
  defaultNextStatus,
  extractSnapshotValue,
  formatCaseAge,
  formatDateTime,
  formatDomainValue,
  formatEventActor,
  formatEventPayloadSummary,
  formatRisk,
  formatSnapshotError,
  formatStatus,
  formatStoplineSummary,
  normalizeList,
  normalizeMetadata,
  riskTone,
  statusTone,
  summarizeArtifactMeta,
  trimText,
} from './ReviewQueueCommon';
import './ReviewQueuePage.css';

function DetailGrid({ rows }) {
  return (
    <dl className="review-queue-detail-grid">
      {rows.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value ?? '-'}</dd>
        </div>
      ))}
    </dl>
  );
}

function JsonPreview({ value }) {
  return (
    <pre className="review-queue-json-preview">
      {JSON.stringify(value || {}, null, 2)}
    </pre>
  );
}

function EmptyText({ children }) {
  return <p className="review-queue-empty">{children}</p>;
}

function ReviewCaseDetailPage() {
  const { caseId } = useParams();
  const location = useLocation();
  const listHref = `/review${location.search || ''}`;

  const [caseDetail, setCaseDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState('');
  const [snapshots, setSnapshots] = useState({ evidence: null, blockedAnswer: null });
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState('');
  const [assistantSummary, setAssistantSummary] = useState(null);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantError, setAssistantError] = useState('');
  const [assistantDrafts, setAssistantDrafts] = useState(null);
  const [assistantDraftLoading, setAssistantDraftLoading] = useState(false);
  const [assistantDraftError, setAssistantDraftError] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [assignReason, setAssignReason] = useState('');
  const [statusTarget, setStatusTarget] = useState('assigned');
  const [statusReason, setStatusReason] = useState('');
  const [noteText, setNoteText] = useState('');
  const [actionType, setActionType] = useState('clinician_handoff');
  const [actionMemo, setActionMemo] = useState('');
  const [assistantDecisionReason, setAssistantDecisionReason] = useState('');
  const [assistantDecisionLoading, setAssistantDecisionLoading] = useState(false);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationError, setMutationError] = useState('');
  const [mutationMessage, setMutationMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadDetail() {
      setDetailLoading(true);
      setDetailError('');
      setMutationError('');
      setMutationMessage('');
      try {
        const detail = await fetchReviewCaseDetail(caseId);
        if (!cancelled) {
          setCaseDetail(detail);
        }
      } catch (loadError) {
        if (!cancelled) {
          setCaseDetail(null);
          setDetailError(loadError.message || '케이스 상세를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    }

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  useEffect(() => {
    if (!caseDetail) {
      setAssignedTo('');
      setStatusTarget('assigned');
      return;
    }
    setAssignedTo(caseDetail.assigned_to || '');
    setStatusTarget(defaultNextStatus(caseDetail.status));
  }, [caseDetail]);

  useEffect(() => {
    if (!caseDetail) {
      setSnapshots({ evidence: null, blockedAnswer: null });
      setSnapshotError('');
      return;
    }

    const metadata = normalizeMetadata(caseDetail.metadata_json);
    const requests = [];
    if (metadata.evidence_snapshot_key) {
      requests.push([
        'evidence',
        fetchReviewSnapshot({
          snapshotType: 'evidence',
          userNo: caseDetail.user_no,
          patientId: caseDetail.patient_id,
          decId: caseDetail.dec_id,
        }),
      ]);
    }
    if (metadata.blocked_answer_snapshot_key) {
      requests.push([
        'blockedAnswer',
        fetchReviewSnapshot({
          snapshotType: 'blocked-answer',
          userNo: caseDetail.user_no,
          patientId: caseDetail.patient_id,
          decId: caseDetail.dec_id,
        }),
      ]);
    }

    if (!requests.length) {
      setSnapshots({ evidence: null, blockedAnswer: null });
      setSnapshotError('');
      return;
    }

    let cancelled = false;
    async function loadSnapshots() {
      setSnapshotLoading(true);
      setSnapshotError('');
      const results = await Promise.allSettled(requests.map(([, request]) => request));
      if (cancelled) {
        return;
      }

      const nextSnapshots = { evidence: null, blockedAnswer: null };
      const errors = [];
      results.forEach((result, index) => {
        const key = requests[index][0];
        if (result.status === 'fulfilled') {
          nextSnapshots[key] = extractSnapshotValue(result.value);
        } else {
          errors.push(formatSnapshotError(key, result.reason));
        }
      });
      setSnapshots(nextSnapshots);
      setSnapshotError(errors.join(' / '));
      setSnapshotLoading(false);
    }

    loadSnapshots();
    return () => {
      cancelled = true;
    };
  }, [caseDetail]);

  useEffect(() => {
    if (!caseId) {
      return;
    }

    let cancelled = false;
    async function loadAssistantSummary() {
      setAssistantLoading(true);
      setAssistantError('');
      try {
        const summary = await fetchReviewAssistantSummary(caseId);
        if (!cancelled) {
          setAssistantSummary(summary);
        }
      } catch (summaryError) {
        if (!cancelled) {
          setAssistantSummary(null);
          setAssistantError(summaryError.message || '검토 보조 요약을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setAssistantLoading(false);
        }
      }
    }

    loadAssistantSummary();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  useEffect(() => {
    if (!caseId) {
      return;
    }

    let cancelled = false;
    async function loadAssistantDrafts() {
      setAssistantDraftLoading(true);
      setAssistantDraftError('');
      try {
        const drafts = await fetchReviewAssistantDrafts(caseId);
        if (!cancelled) {
          setAssistantDrafts(drafts);
        }
      } catch (draftError) {
        if (!cancelled) {
          setAssistantDrafts(null);
          setAssistantDraftError(draftError.message || '검토 초안을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setAssistantDraftLoading(false);
        }
      }
    }

    loadAssistantDrafts();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const metadata = normalizeMetadata(caseDetail?.metadata_json);
  const detailEvents = normalizeList(caseDetail?.events);
  const detailArtifacts = normalizeList(caseDetail?.artifacts);
  const detailNotes = normalizeList(caseDetail?.notes);
  const detailActions = normalizeList(caseDetail?.actions);
  const assignmentEvents = detailEvents.filter((event) => event.event_type === 'assigned');
  const statusEvents = detailEvents.filter((event) => event.event_type === 'status_changed');
  const falsePositiveActions = detailActions.filter((action) => action.action_type === 'false_positive');
  const auditActors = Array.from(new Set(detailEvents.map(formatEventActor).filter((actor) => actor !== '-')));
  const lastEvent = detailEvents[detailEvents.length - 1] || null;
  const evidenceSnapshot = snapshots.evidence || {};
  const blockedAnswerSnapshot = snapshots.blockedAnswer || {};
  const blockedAnswerCandidate = blockedAnswerSnapshot.blocked_answer_candidate
    || metadata.blocked_answer_candidate_preview
    || '';
  const blockedAnswerShown = blockedAnswerSnapshot.blocked_answer_shown
    || metadata.blocked_answer_shown_preview
    || '';

  const basicRows = useMemo(() => {
    if (!caseDetail) {
      return [];
    }
    return [
      { label: '상태', value: formatStatus(caseDetail.status) },
      { label: '트리거', value: formatDomainValue(caseDetail.trigger_type) },
      { label: '소스', value: formatDomainValue(caseDetail.source_service) },
      { label: 'DEC ID', value: caseDetail.dec_id || '-' },
      { label: '사용자', value: caseDetail.user_no ?? '-' },
      { label: '환자', value: caseDetail.patient_id ?? '-' },
      { label: '리포트', value: caseDetail.report_id ?? '-' },
      { label: '세션', value: caseDetail.session_id || '-' },
    ];
  }, [caseDetail]);

  const runCaseMutation = async (mutationFactory, message, onSuccess) => {
    if (!caseId) {
      return false;
    }

    setMutationLoading(true);
    setMutationError('');
    setMutationMessage('');
    try {
      const detail = await mutationFactory(caseId);
      setCaseDetail(detail);
      setMutationMessage(message);
      if (onSuccess) {
        onSuccess();
      }
      return true;
    } catch (mutationFailure) {
      setMutationError(mutationFailure.message || '후속 대응을 저장하지 못했습니다.');
      return false;
    } finally {
      setMutationLoading(false);
    }
  };

  const handleAssignCase = (event) => {
    event.preventDefault();
    const nextAssignee = assignedTo.trim();
    if (!nextAssignee) {
      setMutationError('담당자 ID를 입력해 주세요.');
      return;
    }
    runCaseMutation(
      (targetCaseId) => assignReviewCase(targetCaseId, {
        assigned_to: nextAssignee,
        reason: assignReason.trim() || undefined,
      }),
      '담당자 배정을 저장했습니다.',
      () => setAssignReason('')
    );
  };

  const handleStatusUpdate = (event) => {
    event.preventDefault();
    if (!statusTarget) {
      setMutationError('변경할 상태를 선택해 주세요.');
      return;
    }
    runCaseMutation(
      (targetCaseId) => updateReviewCaseStatus(targetCaseId, {
        status: statusTarget,
        reason: statusReason.trim() || undefined,
      }),
      '케이스 상태를 변경했습니다.',
      () => setStatusReason('')
    );
  };

  const handleCreateNote = (event) => {
    event.preventDefault();
    const trimmedNote = noteText.trim();
    if (!trimmedNote) {
      setMutationError('내부 노트를 입력해 주세요.');
      return;
    }
    runCaseMutation(
      (targetCaseId) => createReviewCaseNote(targetCaseId, {
        note_type: 'triage',
        visibility: 'internal',
        note_text: trimmedNote,
      }),
      '내부 노트를 저장했습니다.',
      () => setNoteText('')
    );
  };

  const handleRecordAction = (event) => {
    event.preventDefault();
    runCaseMutation(
      (targetCaseId) => createReviewCaseAction(targetCaseId, {
        action_type: actionType,
        action_payload_json: actionMemo.trim() ? { memo: actionMemo.trim() } : {},
      }),
      '조치 이력을 기록했습니다.',
      () => setActionMemo('')
    );
  };

  const handleAssistantDraftDecision = async (decision) => {
    if (!caseId || !assistantDrafts) {
      return;
    }

    setAssistantDecisionLoading(true);
    setMutationError('');
    setMutationMessage('');
    try {
      const detail = await recordReviewAssistantDraftDecision(caseId, {
        decision,
        draft_type: 'review_case_drafts',
        source_agent: assistantDrafts.source_service || 'review-assistant-agent',
        reason: assistantDecisionReason.trim() || (decision === 'accepted' ? '검토 초안 채택' : '검토 초안 반려'),
        provenance_json: assistantDrafts.provenance || {},
        draft_content_json: {
          clinician_note_draft: assistantDrafts.clinician_note_draft || '',
          patient_followup_draft: assistantDrafts.patient_followup_draft || '',
          rationale_snippet: assistantDrafts.rationale_snippet || '',
        },
      });
      setCaseDetail(detail);
      setMutationMessage(decision === 'accepted'
        ? '검토 초안 채택을 감사 이벤트로 기록했습니다.'
        : '검토 초안 반려를 감사 이벤트로 기록했습니다.');
      setAssistantDecisionReason('');
    } catch (decisionError) {
      setMutationError(decisionError.message || '검토 초안 결정을 기록하지 못했습니다.');
    } finally {
      setAssistantDecisionLoading(false);
    }
  };

  return (
    <div className="review-queue-page review-case-detail-page">
      <section className="review-queue-hero review-queue-hero-compact">
        <div>
          <Link className="review-queue-back-link" to={listHref}>목록으로</Link>
          <p className="review-queue-eyebrow">CRV-UX01 / CASE DETAIL</p>
          <h1>{caseDetail?.case_no || '검토 케이스 상세'}</h1>
          <p className="review-queue-hero-copy">
            케이스 발생 이유, 근거 스냅샷, 차단 답변, 감사 이력과 후속 대응을 한 화면에서 확인합니다.
          </p>
        </div>
        {caseDetail ? (
          <div className="review-queue-hero-actions review-queue-case-status-stack">
            <div className="review-queue-chip-row">
              <ReviewChip tone={statusTone(caseDetail.status)}>{formatStatus(caseDetail.status)}</ReviewChip>
              <ReviewChip type="risk" tone={riskTone(caseDetail.risk_level)}>{formatRisk(caseDetail.risk_level)}</ReviewChip>
            </div>
            <div className="review-queue-sync-block">
              <strong>{caseDetail.assigned_to || '미배정'}</strong>
              <span>{formatDateTime(caseDetail.updated_at)}</span>
            </div>
          </div>
        ) : null}
      </section>

      <InlineMessage kind="status">{detailLoading ? '케이스 상세를 불러오는 중입니다...' : ''}</InlineMessage>
      <InlineMessage kind="error">{detailError}</InlineMessage>

      {caseDetail ? (
        <section className="review-queue-detail-shell">
          <main className="review-queue-detail-main">
            <article className="review-queue-card">
              <SectionTitle label="먼저 확인할 내용" marker="CRV-RA02" />
              <InlineMessage kind="status">{assistantLoading ? '검토 보조 요약을 생성하는 중입니다...' : ''}</InlineMessage>
              <InlineMessage kind="warning">{assistantError}</InlineMessage>
              {assistantSummary ? (
                <div className="review-queue-next-read-list">
                  <p className="review-queue-assistant-summary">{assistantSummary.summary || '-'}</p>
                  {normalizeList(assistantSummary.recommended_next_reads).map((block) => (
                    <article key={block.id || block.title} className="review-queue-next-read-card">
                      <div>
                        <strong>{block.title || '확인 항목'}</strong>
                        <span>{formatDomainValue(block.priority)}</span>
                      </div>
                      <p>{block.reason || '-'}</p>
                      {normalizeList(block.evidence).length ? (
                        <ul>
                          {normalizeList(block.evidence).map((item, index) => (
                            <li key={`${block.id || 'next-read'}-${index}`}>{trimText(item, 180)}</li>
                          ))}
                        </ul>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : (
                <div className="review-queue-next-read-list">
                  <article className="review-queue-next-read-card">
                    <div>
                      <strong>Stop-the-line 사유 먼저 확인</strong>
                      <span>높음</span>
                    </div>
                    <p>{formatStoplineSummary(caseDetail.summary_text)}</p>
                  </article>
                </div>
              )}
            </article>

            <article className="review-queue-card">
              <SectionTitle label="기본 정보" marker={caseDetail.case_no || caseDetail.case_id} />
              <DetailGrid rows={basicRows} />
              <p className="review-queue-summary-text">
                {metadata.question_preview || metadata.question || '질문 원문 정보가 없습니다.'}
              </p>
            </article>

            <article className="review-queue-card">
              <SectionTitle label="근거 스냅샷" marker="Evidence" />
              <InlineMessage kind="status">{snapshotLoading ? '스냅샷을 불러오는 중입니다...' : ''}</InlineMessage>
              <InlineMessage kind="warning">{snapshotError}</InlineMessage>
              <DetailGrid
                rows={[
                  { label: 'RAG 상태', value: formatDomainValue(evidenceSnapshot.rag_status || metadata.rag_status) },
                  { label: 'RAG 사유', value: formatDomainValue(evidenceSnapshot.rag_reason || metadata.rag_reason) },
                  { label: '근거 모드', value: formatDomainValue(evidenceSnapshot.evidence_mode || metadata.evidence_mode) },
                  {
                    label: '근거 관련성',
                    value: formatDomainValue(evidenceSnapshot.evidence_relevance_status || metadata.evidence_relevance_status),
                  },
                ]}
              />
              {Object.keys(evidenceSnapshot).length ? <JsonPreview value={evidenceSnapshot} /> : null}
            </article>

            <article className="review-queue-card">
              <SectionTitle label="차단 답변 스냅샷" marker="Blocked Answer" />
              <div className="review-queue-answer-grid">
                <div>
                  <h3>차단 직전 후보 답변</h3>
                  <p>{blockedAnswerCandidate || '후보 답변 스냅샷이 없습니다.'}</p>
                </div>
                <div>
                  <h3>사용자 노출 안내문</h3>
                  <p>{blockedAnswerShown || '사용자 노출 안내문 스냅샷이 없습니다.'}</p>
                </div>
              </div>
            </article>

            <article className="review-queue-card">
              <SectionTitle label="아티팩트 참조" marker="CRV-CC03" />
              {detailArtifacts.length ? (
                <div className="review-queue-artifact-list">
                  {detailArtifacts.map((artifact) => (
                    <div key={artifact.artifact_id || artifact.artifact_key} className="review-queue-artifact-row">
                      <span>{ARTIFACT_LABELS[artifact.artifact_type] || formatDomainValue(artifact.artifact_type)}</span>
                      <div className="review-queue-artifact-copy">
                        <strong>{artifact.artifact_key || '-'}</strong>
                        <small>{summarizeArtifactMeta(artifact)}</small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyText>등록된 아티팩트 참조가 없습니다.</EmptyText>
              )}
            </article>

            {assistantDrafts ? (
              <article className="review-queue-card review-queue-draft-panel">
                <SectionTitle label="검토 초안" marker="CRV-RA03" />
                <div className="review-queue-draft-grid">
                  <article className="review-queue-draft-card">
                    <h3>검토 노트 초안</h3>
                    <pre>{assistantDrafts.clinician_note_draft || '-'}</pre>
                  </article>
                  <article className="review-queue-draft-card">
                    <h3>환자 후속 안내 초안</h3>
                    <pre>{assistantDrafts.patient_followup_draft || '-'}</pre>
                  </article>
                </div>
                <article className="review-queue-draft-rationale">
                  <h3>작성 근거</h3>
                  <p>{assistantDrafts.rationale_snippet || '-'}</p>
                </article>
              </article>
            ) : (
              <article className="review-queue-card">
                <SectionTitle label="검토 초안" marker="CRV-RA03" />
                <InlineMessage kind="status">{assistantDraftLoading ? '검토 초안을 생성하는 중입니다...' : ''}</InlineMessage>
                <InlineMessage kind="warning">{assistantDraftError}</InlineMessage>
                {!assistantDraftLoading && !assistantDraftError ? <EmptyText>검토 초안이 없습니다.</EmptyText> : null}
              </article>
            )}

            <article className="review-queue-card review-queue-ops-panel">
              <SectionTitle label="운영 추적" marker="CRV-CC05" />
              <dl className="review-queue-ops-grid">
                <div>
                  <dt>미해결 경과</dt>
                  <dd>{formatCaseAge(caseDetail)}</dd>
                </div>
                <div>
                  <dt>담당자</dt>
                  <dd>{caseDetail.assigned_to || '미배정'}</dd>
                </div>
                <div>
                  <dt>감사 이벤트</dt>
                  <dd>{detailEvents.length}개</dd>
                </div>
                <div>
                  <dt>참여 행위자</dt>
                  <dd>{auditActors.length ? `${auditActors.length}명/서비스` : '-'}</dd>
                </div>
                <div>
                  <dt>노트/조치</dt>
                  <dd>{detailNotes.length} / {detailActions.length}</dd>
                </div>
                <div>
                  <dt>오탐 처리</dt>
                  <dd>{falsePositiveActions.length ? `${falsePositiveActions.length}건` : '없음'}</dd>
                </div>
              </dl>

              <div className="review-queue-ops-split">
                <div>
                  <h3>배정 이력</h3>
                  {assignmentEvents.length ? (
                    <ul>
                      {assignmentEvents.map((event, index) => (
                        <li key={event.event_id || `assignment-${index}`}>
                          <strong>{formatDateTime(event.created_at)}</strong>
                          <span>{formatEventPayloadSummary(event) || '배정 변경'}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>아직 배정 이벤트가 없습니다.</p>
                  )}
                </div>
                <div>
                  <h3>상태 감사</h3>
                  {statusEvents.length ? (
                    <ul>
                      {statusEvents.map((event, index) => (
                        <li key={event.event_id || `status-${index}`}>
                          <strong>{formatDateTime(event.created_at)}</strong>
                          <span>{formatEventPayloadSummary(event) || '상태 변경'}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>아직 상태 변경 이벤트가 없습니다.</p>
                  )}
                </div>
              </div>

              <div className="review-queue-audit-foot">
                <span>마지막 이벤트</span>
                <strong>
                  {lastEvent
                    ? `${EVENT_LABELS[lastEvent.event_type] || formatDomainValue(lastEvent.event_type)} / ${formatEventActor(lastEvent)} / ${formatDateTime(lastEvent.created_at)}`
                    : '-'}
                </strong>
              </div>
            </article>

            <article className="review-queue-card">
              <SectionTitle label="감사 타임라인" marker={`${detailEvents.length}개 이벤트`} />
              {detailEvents.length ? (
                <ol className="review-queue-timeline">
                  {detailEvents.map((event, index) => (
                    <li key={event.event_id || `event-${index}`}>
                      <strong>{EVENT_LABELS[event.event_type] || formatDomainValue(event.event_type)}</strong>
                      <span>{formatEventActor(event)} / {formatDateTime(event.created_at)}</span>
                      <p>{formatEventPayloadSummary(event) || '-'}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <EmptyText>감사 이벤트가 없습니다.</EmptyText>
              )}
            </article>

            <article className="review-queue-card">
              <SectionTitle label="노트 및 조치" marker={`${detailNotes.length} / ${detailActions.length}`} />
              <div className="review-queue-notes-actions-grid">
                <section>
                  <h3>내부 노트</h3>
                  {detailNotes.length ? (
                    <div className="review-queue-note-list">
                      {detailNotes.map((note) => (
                        <article key={note.note_id} className="review-queue-note-row">
                          <div>
                            <strong>{note.note_type ? formatDomainValue(note.note_type) : '노트'}</strong>
                            <span>{note.author_id || '-'} / {formatDateTime(note.created_at)}</span>
                          </div>
                          <p>{note.note_text}</p>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <EmptyText>저장된 내부 노트가 없습니다.</EmptyText>
                  )}
                </section>

                <section>
                  <h3>조치 기록</h3>
                  {detailActions.length ? (
                    <div className="review-queue-action-list">
                      {detailActions.map((action) => (
                        <article key={action.action_id} className="review-queue-action-row">
                          <div>
                            <strong>{action.action_type ? ACTION_LABELS[action.action_type] || formatDomainValue(action.action_type) : '조치'}</strong>
                            <span>{action.executed_by || '-'} / {formatDateTime(action.executed_at)}</span>
                          </div>
                          <pre>{JSON.stringify(action.action_payload_json || {}, null, 2)}</pre>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <EmptyText>저장된 조치 기록이 없습니다.</EmptyText>
                  )}
                </section>
              </div>
            </article>
          </main>

          <aside className="review-queue-action-panel">
            <article className="review-queue-card review-queue-workflow-panel">
              <SectionTitle label="후속 대응" marker="CRV-CC04" />
              <InlineMessage kind="status">{mutationMessage}</InlineMessage>
              <InlineMessage kind="error">{mutationError}</InlineMessage>

              <div className="review-queue-workflow-stack">
                <form className="review-queue-workflow-form" onSubmit={handleAssignCase}>
                  <label>
                    <span>담당자</span>
                    <input
                      type="text"
                      value={assignedTo}
                      onChange={(event) => setAssignedTo(event.target.value)}
                      placeholder="clinician-01"
                    />
                  </label>
                  <label>
                    <span>배정 사유</span>
                    <input
                      type="text"
                      value={assignReason}
                      onChange={(event) => setAssignReason(event.target.value)}
                      placeholder="배정 사유"
                    />
                  </label>
                  <button type="submit" disabled={mutationLoading}>배정</button>
                </form>

                <form className="review-queue-workflow-form" onSubmit={handleStatusUpdate}>
                  <label>
                    <span>상태</span>
                    <select value={statusTarget} onChange={(event) => setStatusTarget(event.target.value)}>
                      {STATUS_WRITE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>사유</span>
                    <input
                      type="text"
                      value={statusReason}
                      onChange={(event) => setStatusReason(event.target.value)}
                      placeholder="상태 변경 사유"
                    />
                  </label>
                  <button type="submit" disabled={mutationLoading}>상태 변경</button>
                </form>

                <form className="review-queue-workflow-form" onSubmit={handleCreateNote}>
                  <label>
                    <span>내부 노트</span>
                    <textarea
                      value={noteText}
                      onChange={(event) => setNoteText(event.target.value)}
                      placeholder="분류/검토 노트를 입력"
                      rows={4}
                    />
                  </label>
                  <button type="submit" disabled={mutationLoading}>노트 저장</button>
                </form>

                <form className="review-queue-workflow-form" onSubmit={handleRecordAction}>
                  <label>
                    <span>조치 유형</span>
                    <select value={actionType} onChange={(event) => setActionType(event.target.value)}>
                      {ACTION_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>조치 메모</span>
                    <input
                      type="text"
                      value={actionMemo}
                      onChange={(event) => setActionMemo(event.target.value)}
                      placeholder="인계 채널, 연락 결과 등"
                    />
                  </label>
                  <button type="submit" disabled={mutationLoading}>조치 기록</button>
                </form>
              </div>
            </article>

            <article className="review-queue-card review-queue-draft-decision-panel">
              <SectionTitle label="초안 결정" marker="CRV-RA04" />
              <InlineMessage kind="status">{assistantDraftLoading ? '검토 초안을 생성하는 중입니다...' : ''}</InlineMessage>
              <InlineMessage kind="warning">{assistantDraftError}</InlineMessage>
              <label>
                <span>채택/반려 사유</span>
                <input
                  type="text"
                  value={assistantDecisionReason}
                  onChange={(event) => setAssistantDecisionReason(event.target.value)}
                  placeholder="감사 이벤트에 남길 사유"
                  disabled={!assistantDrafts}
                />
              </label>
              <div className="review-queue-draft-decision-actions">
                <button
                  type="button"
                  onClick={() => handleAssistantDraftDecision('accepted')}
                  disabled={!assistantDrafts || assistantDecisionLoading || mutationLoading}
                >
                  채택 기록
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => handleAssistantDraftDecision('rejected')}
                  disabled={!assistantDrafts || assistantDecisionLoading || mutationLoading}
                >
                  반려 기록
                </button>
              </div>
              <p>초안 결정은 감사 이벤트만 추가하며 상태, 노트, 조치를 직접 변경하지 않습니다.</p>
            </article>
          </aside>
        </section>
      ) : null}
    </div>
  );
}

export default ReviewCaseDetailPage;
